import React, { useRef, useState } from "react";
import { useFileUpload, type UploadOptions } from "../../context/FileUploadContext";
import { useNotification } from "../../context/NotificationContext";
import type { Document } from "@/services/api/documents/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUpload,
  faPaperclip,
  faExclamationTriangle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import type { IngestAPIResponse } from "@/services/api/data-hub/types";

// Helper function to convert MIME types to user-friendly descriptions
const getFriendlyFileTypes = (mimeTypes: string[] | undefined): string => {
  if (!mimeTypes || mimeTypes.length === 0) return "All file types accepted";

  const mimeTypeMap: { [key: string]: string } = {
    "application/pdf": "PDF documents",
    "image/jpeg": "JPEG",
    "image/png": "PNG",
    "image/gif": "GIF",
    "application/msword": "Word Documents",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word Documents",
    "application/vnd.ms-excel": "Excel Spreadsheets",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "Excel Spreadsheets",
    "application/vnd.ms-powerpoint": "PowerPoint Presentations",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "PowerPoint Presentations",
    "text/plain": "TXT",
    "text/csv": "CSV",
    "application/json": "JSON",
  };

  const friendlyTypes = mimeTypes.map(
    (mimeType) => mimeTypeMap[mimeType] || mimeType
  );
  // Remove duplicates using Set and sort for consistent ordering
  const uniqueTypes = [...new Set(friendlyTypes)].sort();
  return uniqueTypes.join(", ");
};

interface FileUploaderProps {
  // Function that handles the actual API call to upload the file
  // It can return either a full Document or an IngestField; we'll normalize it before calling the upload hook.
  uploadFunction: (file: File) => Promise<Document | IngestAPIResponse>;
  // Optional props
  acceptedMimeTypes?: string[];
  /** Replaces the generated list of accepted types in the hint. */
  acceptedTypesLabel?: string;
  maxSize?: number;
  multiple?: boolean;
  className?: string;
  // Optional callback when upload is complete
  onUploadComplete?: (result: Document | IngestAPIResponse) => void;
  // Optional callback when upload fails
  onUploadError?: (error: Error) => void;
  // Optional callback for deferred upload (when provided, uploadFunction is not called immediately)
  onFileSelected?: (file: File) => void;
  /** Called once per pick/drop with every file (deferred upload). */
  onFilesSelected?: (files: File[]) => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  uploadFunction,
  acceptedMimeTypes,
  acceptedTypesLabel,
  maxSize = 20 * 1024 * 1024, // 20MB default
  multiple = false,
  className = "",
  onUploadComplete,
  onUploadError,
  onFileSelected,
  onFilesSelected,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { showSuccess, showError } = useNotification();

  // Set up upload options
  const options: UploadOptions = {
    acceptedMimeTypes,
    maxSize,
    multiple,
  };

  // Use our custom upload hook
  const { uploadFile, uploading, error, reset } = useFileUpload();

  // Create upload function that handles notifications
  const uploadWithNotifications = async (file: File): Promise<Document | IngestAPIResponse> => {
    try {
      // Validate file size
      if (file.size > maxSize) {
        throw new Error(
          `File size (${(file.size / (1024 * 1024)).toFixed(
            2
          )}MB) exceeds maximum allowed size (${(
            maxSize /
            (1024 * 1024)
          ).toFixed(2)}MB)`
        );
      }

      // Validate file type
      if (
        acceptedMimeTypes &&
        acceptedMimeTypes.length > 0 &&
        !acceptedMimeTypes.includes(file.type)
      ) {
        throw new Error(
          `File type "${
            file.type
          }" is not allowed. Allowed types: ${acceptedMimeTypes.join(", ")}`
        );
      }

      // Upload the file
      const result = await uploadFile(file, uploadFunction, options, {
        onSuccess: (uploadedDoc) => {
          // Show success notification
          showSuccess(
            "Upload successful",
            `${file.name} has been uploaded successfully`,
            {
              duration: 4000,
            }
          );

          // Call success callback if provided
          if (onUploadComplete) {
            onUploadComplete(uploadedDoc);
          }
        },
        onError: (error) => {
          // Show error notification
          showError(
            "Upload failed",
            error.message || "There was a problem uploading your document",
            {
              duration: 8000,
            }
          );

          // Call error callback if provided
          if (onUploadError) {
            onUploadError(error);
          }

          throw error;
        },
      });

      if (!result) {
        throw new Error("Upload failed - no response from server");
      }

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";

      // Show error notification
      showError("Upload failed", errorMessage, {
        duration: 8000,
      });

      // Call error callback if provided
      if (onUploadError) {
        onUploadError(error instanceof Error ? error : new Error(errorMessage));
      }

      throw error;
    }
  };

  // Handle file selection
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (onFilesSelected || onFileSelected) {
      const picked = multiple
        ? Array.from(files)
        : [files[0]];
      if (onFilesSelected) {
        onFilesSelected(picked);
      } else {
        for (const file of picked) {
          onFileSelected!(file);
        }
      }
    } else {
      // Immediate upload mode - use the original logic
      if (multiple) {
        // Handle multiple files
        for (let i = 0; i < files.length; i++) {
          try {
            await uploadWithNotifications(files[i]);
          } catch (error) {
            console.error("Upload error:", error);
          }
        }
      } else {
        // Handle single file
        try {
          await uploadWithNotifications(files[0]);
        } catch (error) {
          console.error("Upload error:", error);
        }
      }
    }

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    if (onFilesSelected || onFileSelected) {
      const picked = multiple ? Array.from(files) : [files[0]];
      if (onFilesSelected) {
        onFilesSelected(picked);
      } else {
        for (const file of picked) {
          onFileSelected!(file);
        }
      }
    } else {
      // Immediate upload mode - use the original logic
      if (multiple) {
        // Handle multiple files
        for (let i = 0; i < files.length; i++) {
          try {
            await uploadWithNotifications(files[i]);
          } catch (error) {
            console.error("Upload error:", error);
          }
        }
      } else {
        // Handle single file (just the first one)
        try {
          await uploadWithNotifications(files[0]);
        } catch (error) {
          console.error("Upload error:", error);
        }
      }
    }
  };

  // Trigger file input click
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Reset error state
  const handleReset = () => {
    setHasError(false);
    setErrorMessage("");
    reset();
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Drag and drop area */}
      <div
        className={`  rounded-lg p-6 px-3 text-center cursor-pointer ${
          isDragging
            ? " bg-slate-300  dark:bg-indigo-900/20"
            : hasError
            ? " bg-red-100  dark:bg-red-900/20"
            : "bg-slate-100  dark:bg-slate-800 "
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
          multiple={multiple}
          accept={acceptedMimeTypes?.join(",")}
        />

        {hasError ? (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="mx-auto h-12 w-12 text-red-400 dark:text-red-300"
          />
        ) : (
          <FontAwesomeIcon
            icon={faCloudUpload}
            className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300"
          />
        )}

        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          {uploading
            ? "Uploading..."
            : hasError
            ? "Upload failed"
            : "Drag and drop files here"}
        </h3>

        {hasError ? (
          <div>
            <p className="mt-1 text-sm text-red-500 dark:text-red-400">
              {errorMessage || "There was a problem with your upload"}
            </p>
            <button
              type="button"
              className="mt-4 inline-flex cursor-pointer items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-red-700 dark:hover:bg-red-600 dark:focus:ring-red-400 dark:focus:ring-offset-slate-900"
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="font-mono text-xs text-gray-400 uppercase mt-3">
              or
            </div>
            <button
              type="button"
              className="mb-4 mt-4 inline-flex cursor-pointer items-center px-4 py-2 text-sm font-medium text-slate-900 bg-slate-200 rounded-md hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 dark:bg-indigo-600 dark:text-white dark:hover:bg-indigo-500 dark:focus:ring-indigo-400 dark:focus:ring-offset-slate-800"
              onClick={(e) => {
                e.stopPropagation();
                handleButtonClick();
              }}
              disabled={uploading}
            >
              <FontAwesomeIcon icon={faPaperclip} className="mr-2" />
              {uploading ? "Uploading..." : "Select Files"}
            </button>
            <div className="flex flex-col gap-2 mt-6">
              <p className=" font-mono text-xs text-gray-500 dark:text-gray-400">
                {multiple
                  ? "You can upload multiple files"
                  : "You can only upload one file at a time"}
              </p>
              <p className=" font-mono text-xs text-gray-500 dark:text-gray-400">
                {acceptedTypesLabel ?? getFriendlyFileTypes(acceptedMimeTypes)}
              </p>
              {maxSize && (
                <p className=" font-mono text-xs text-gray-500 dark:text-gray-400">
                  Maximum file size: {(maxSize / (1024 * 1024)).toFixed(2)}MB
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
